import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import type { UserRole } from '../types/auth';
import './ProtectedRoute.css';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: UserRole;
  redirectTo?: string;
}

/**
 * ProtectedRoute component that wraps routes requiring authentication
 *
 * Features:
 * - Checks authentication status
 * - Redirects unauthenticated users to login
 * - Handles loading states during auth verification
 * - Supports role-based access control
 * - Preserves intended destination for post-login redirect
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredRole,
  redirectTo = '/login',
}) => {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();

  // Show loading spinner while authentication is being verified
  if (isLoading) {
    return (
      <div
        className="protected-route-loading"
        data-testid="protected-route-loading"
      >
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Verifying authentication...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  // Preserve the current location so we can redirect back after login
  if (!isAuthenticated) {
    return (
      <Navigate
        to={redirectTo}
        state={{ from: location }}
        replace
        data-testid="redirect-to-login"
      />
    );
  }

  // Redirect to activation if account is not active, unless we're already going there
  if (user && !user.isActive && location.pathname !== '/activation') {
    return <Navigate to="/activation" state={{ from: location }} replace />;
  }

  // Check role-based access if required role is specified
  if (requiredRole && user) {
    const hasRequiredRole = checkUserRole(user.role, requiredRole);

    if (!hasRequiredRole) {
      return (
        <Navigate
          to="/unauthorized"
          state={{ from: location, requiredRole }}
          replace
          data-testid="redirect-to-unauthorized"
        />
      );
    }
  }

  // User is authenticated and has required role (if specified)
  return <>{children}</>;
};

/**
 * Helper function to check if user has required role
 * Implements role hierarchy: admin > user
 */
const checkUserRole = (userRole: UserRole, requiredRole: UserRole): boolean => {
  // Define role hierarchy
  const roleHierarchy: Record<UserRole, number> = {
    user: 1,
    admin: 2,
  };

  const userRoleLevel = roleHierarchy[userRole] || 0;
  const requiredRoleLevel = roleHierarchy[requiredRole] || 0;

  // User must have equal or higher role level
  return userRoleLevel >= requiredRoleLevel;
};

export default ProtectedRoute;
